///
/// Copyright (c) 2017-present, Siklu Ltd.
/// All rights reserved.
///

import { GtthAbstractCommand } from '../../abstract-command';
import { ActivityDTO } from '../../../../../services/project-socket/activity-dto.model';
import { LinkFeature } from '../../../entities/link/link-feature';
import { TopologyLinkTypeEnum } from '../../../../../services/project-socket/dto/topology-types.model';
import { isDefined } from '@siklu/sui-lib/utils';

export class GtthModifyLinkInfoCommand extends GtthAbstractCommand {
    private newLinkType: TopologyLinkTypeEnum;
    private oldLinkType: TopologyLinkTypeEnum;
    private newUsedProd0: number;
    private newUsedProd1: number;
    private oldUsedProd0: number;
    private oldUsedProd1: number;
    private newUsedAnt0: number;
    private oldUsedAnt0: number;
    private newUsedAnt1: number;
    private oldUsedAnt1: number;
    private oldLinkIsDisable: boolean;
    private newLinkIsDisable: boolean;
    private oldIsUserForced: boolean;
    private newIsUserForced: boolean;
    private oldIsInstalled: boolean;
    private newIsInstalled: boolean;
    private objectChanged: boolean;
    private freeezeChanged: boolean;

    private oldSectorIndex: number;
    private oldSectorApIndex: number;
    private oldSectorDirection: number;
    private newSectorIndex: number;
    private newSectorApIndex: number;
    private newSectorDirection: number;

    private linkId: number;

    getCommandName(): string {
        return 'GtthModifyLinkInfoCommand';
    }

    create(linkId: number): this {
        this.linkId = linkId;
        return this;
    }

    setOldLinkType(oldwLinkType: TopologyLinkTypeEnum) {
        this.oldLinkType = oldwLinkType;
    }

    setNewLinkType(newLinkType: TopologyLinkTypeEnum) {
        this.newLinkType = newLinkType;
        this.historyService.logicalTopology.getLink(this.linkId).setLinkType(this.newLinkType);
    }

    setOldUsedProd0(oldUsedProd: number) {
        this.oldUsedProd0 = oldUsedProd;
    }

    setOldUsedProd1(oldUsedProd: number) {
        this.oldUsedProd1 = oldUsedProd;
    }

    setNewUsedProd0(newUsedProd: number) {
        this.newUsedProd0 = newUsedProd;
        this.historyService.logicalTopology.getLink(this.linkId).setUsedProd0(this.newUsedProd0);
    }

    setNewUsedProd1(newUsedProd: number) {
        this.newUsedProd1 = newUsedProd;
        this.historyService.logicalTopology.getLink(this.linkId).setUsedProd1(this.newUsedProd1);
    }

    setOldUsedAnt0(oldUsedAnt: number) {
        this.oldUsedAnt0 = oldUsedAnt;
    }

    setNewUsedAnt0(newUsedAnt: number) {
        this.newUsedAnt0 = newUsedAnt;
        this.historyService.logicalTopology.getLink(this.linkId).setUsedAnt0(this.newUsedAnt0);
    }

    setOldUsedAnt1(oldUsedAnt: number) {
        this.oldUsedAnt1 = oldUsedAnt;
    }

    setNewUsedAnt1(newUsedAnt: number) {
        this.newUsedAnt1 = newUsedAnt;
        this.historyService.logicalTopology.getLink(this.linkId).setUsedAnt1(this.newUsedAnt1);
    }

    setOldLinkIsDisable(oldLinkIsDisable: boolean) {
        this.oldLinkIsDisable = oldLinkIsDisable;
    }

    setNewLinkIsDisable(newLinkIsDisable: boolean) {
        this.newLinkIsDisable = newLinkIsDisable;
        this.historyService.logicalTopology.getLink(this.linkId).setLinkIsDisabled(this.newLinkIsDisable);
    }

    setOldIsUserForced(oldIsUserForced: boolean) {
        this.oldIsUserForced = oldIsUserForced;
    }

    setNewIsUserForced(newIsUserForced: boolean) {
        this.newIsUserForced = newIsUserForced;
        this.historyService.logicalTopology.getLink(this.linkId).setUserForced(this.newIsUserForced);
    }

    setOldSectorInfo(index: number, apIndex: number, direction: number) {
        this.oldSectorIndex = index;
        this.oldSectorApIndex = apIndex;
        this.oldSectorDirection = direction;
    }

    setNewSectorInfo(index: number, apIndex: number, direction: number) {
        this.newSectorIndex = index;
        this.newSectorApIndex = apIndex;
        this.newSectorDirection = direction;
    }

    setOldIsInstalled(oldIsInstalled: boolean) {
        this.oldIsInstalled = oldIsInstalled;
    }

    setNewIsInstalled(newIsInstalled: boolean) {
        this.newIsInstalled = newIsInstalled;
        this.historyService.logicalTopology.getLink(this.linkId).setIsInstalled(this.newIsInstalled);
    }

    doCommand(): void {
        const link = this.historyService.logicalTopology.getLink(this.linkId);

        this.objectChanged = this.setNewParameters(link);
        this.freeezeChanged = this.setFreezeParameters(link);

        if (this.objectChanged || this.freeezeChanged) {
            const activity = this.createActivityDTO(false);
            activity.links = [link.toGeoJson()];
            this.sendActivity(activity);
        } else if (this.commandResolve) {
            this.commandResolve(false);
        }
    }

    undoCommand(): void {
        const link = this.historyService.logicalTopology.getLink(this.linkId);

        this.setOldParameters(link);

        const activity = this.createActivityDTO(false);
        activity.links = [link.toGeoJson()];
        this.sendActivity(activity);
    }

    doCommandActivity(activityDto: ActivityDTO): void {
        if (!activityDto.successful) {
            if (this.linkId) {
                this.setOldParameters(this.historyService.logicalTopology.getLink(activityDto.links[0].getId()));
            }
        } else {
            const link = this.historyService.logicalTopology.getLink(activityDto.links[0].getId());
            link.parseGeoJson(activityDto.links[0]);
            this.historyService.logicalTopology.updateLinkSector(link);
        }
        if (this.commandResolve) {
            this.commandResolve(activityDto.successful);
        }
    }

    undoCommandActivity(activityDto: ActivityDTO): void {
        if (!activityDto.successful) {
            if (this.linkId) {
                const link = this.historyService.logicalTopology.getLink(activityDto.links[0].getId());
                this.setNewParameters(link);
                this.setFreezeParameters(link);
            }
        } else {
            const link = this.historyService.logicalTopology.getLink(activityDto.links[0].getId());
            link.parseGeoJson(activityDto.links[0]);
            this.historyService.logicalTopology.updateLinkSector(link);
        }
        if (this.commandResolve) {
            this.commandResolve(activityDto.successful);
        }
    }

    isDisabledInReadOnlyMode(): boolean {
        return true;
    }

    isClearDesign(): boolean {
        return this.objectChanged;
    }

    isModifyTopology(): boolean {
        return true;
    }

    linkWasUnfrozen() {
        return this.oldIsUserForced && !this.newIsUserForced;
    }

    private setNewParameters(link: LinkFeature): boolean {
        let objectChanged = false;

        if (isDefined(this.newLinkType) && isDefined(this.oldLinkType) && this.newLinkType !== this.oldLinkType) {
            link.setLinkType(this.newLinkType);
            objectChanged = true;
        }

        if (
            isDefined(this.newUsedProd0) &&
            isDefined(this.newUsedProd1) &&
            isDefined(this.oldUsedProd0) &&
            isDefined(this.oldUsedProd1) &&
            isDefined(this.newUsedAnt0) &&
            isDefined(this.oldUsedAnt0) &&
            isDefined(this.newUsedAnt1) &&
            isDefined(this.oldUsedAnt1) &&
            (this.newUsedProd0 !== this.oldUsedProd0 ||
                this.newUsedProd1 !== this.oldUsedProd1 ||
                this.newUsedAnt0 !== this.oldUsedAnt0 ||
                this.newUsedAnt1 !== this.oldUsedAnt1)
        ) {
            link.setUsedProd0(this.newUsedProd0);
            link.setUsedProd1(this.newUsedProd1);
            link.setUsedAnt0(this.newUsedAnt0);
            link.setUsedAnt1(this.newUsedAnt1);
            objectChanged = true;
        }

        if (isDefined(this.newLinkIsDisable) && isDefined(this.oldLinkIsDisable) && this.newLinkIsDisable !== this.oldLinkIsDisable) {
            link.setLinkIsDisabled(this.newLinkIsDisable);
            objectChanged = true;
        }

        if (isDefined(this.oldSectorIndex) && isDefined(this.newSectorIndex) && this.oldSectorIndex !== this.newSectorIndex) {
            link.setSectorIndex(this.newSectorIndex);
            objectChanged = true;
        }

        if (isDefined(this.oldSectorApIndex) && isDefined(this.newSectorApIndex) && this.oldSectorApIndex !== this.newSectorApIndex) {
            link.setSectorApIndex(this.newSectorApIndex);
            objectChanged = true;
        }

        if (
            isDefined(this.oldSectorDirection) &&
            isDefined(this.newSectorDirection) &&
            this.oldSectorDirection !== this.newSectorDirection
        ) {
            link.setSectorDirection(this.newSectorDirection);
            objectChanged = true;
        }

        if (isDefined(this.oldIsInstalled) && isDefined(this.newIsInstalled) && this.oldIsInstalled !== this.newIsInstalled) {
            link.setIsInstalled(this.newIsInstalled);
            objectChanged = true;
        }

        return objectChanged;
    }

    private setFreezeParameters(link: LinkFeature): boolean {
        let freezeChanged = false;

        if (isDefined(this.newIsUserForced) && isDefined(this.oldIsUserForced) && this.newIsUserForced !== this.oldIsUserForced) {
            link.setUserForced(this.newIsUserForced);
            freezeChanged = true;
        }

        return freezeChanged;
    }

    private setOldParameters(link: LinkFeature) {
        if (isDefined(this.newLinkType) && isDefined(this.oldLinkType) && this.newLinkType !== this.oldLinkType) {
            link.setLinkType(this.oldLinkType);
        }

        if (
            isDefined(this.newUsedProd0) &&
            isDefined(this.newUsedProd1) &&
            isDefined(this.oldUsedProd0) &&
            isDefined(this.oldUsedProd1) &&
            isDefined(this.newUsedAnt0) &&
            isDefined(this.oldUsedAnt0) &&
            isDefined(this.newUsedAnt1) &&
            isDefined(this.oldUsedAnt1) &&
            (this.newUsedProd0 !== this.oldUsedProd0 ||
                this.newUsedProd1 !== this.oldUsedProd1 ||
                this.newUsedAnt0 !== this.oldUsedAnt0 ||
                this.newUsedAnt1 !== this.oldUsedAnt1)
        ) {
            link.setUsedProd0(this.oldUsedProd0);
            link.setUsedProd1(this.oldUsedProd1);
            link.setUsedAnt0(this.oldUsedAnt0);
            link.setUsedAnt1(this.oldUsedAnt1);
        }

        if (isDefined(this.newLinkIsDisable) && isDefined(this.oldLinkIsDisable) && this.newLinkIsDisable !== this.oldLinkIsDisable) {
            link.setLinkIsDisabled(this.oldLinkIsDisable);
        }

        if (isDefined(this.newIsUserForced) && isDefined(this.oldIsUserForced) && this.newIsUserForced !== this.oldIsUserForced) {
            link.setUserForced(this.oldIsUserForced);
        }

        if (isDefined(this.oldSectorIndex) && isDefined(this.newSectorIndex) && this.oldSectorIndex !== this.newSectorIndex) {
            link.setSectorIndex(this.oldSectorIndex);
        }

        if (isDefined(this.oldSectorApIndex) && isDefined(this.newSectorApIndex) && this.oldSectorApIndex !== this.newSectorApIndex) {
            link.setSectorApIndex(this.oldSectorApIndex);
        }

        if (
            isDefined(this.oldSectorDirection) &&
            isDefined(this.newSectorDirection) &&
            this.oldSectorDirection !== this.newSectorDirection
        ) {
            link.setSectorDirection(this.oldSectorDirection);
        }
    }
}
